import React, { useEffect, useState } from 'react';
import { aboutContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, pickCmsText, makeSectionCopyPicker } from '../services/publicContentService';

const About = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [cms, setCms] = useState(null); // { ar, en } | null
  const [sc, setSc] = useState({});

  useEffect(() => {
    let alive = true;
    fetchPublicSettings('company_info').then((v) => {
      if (!alive) return;
      // Only accept CMS content when both AR and EN look like real paragraphs
      // (avoids leftover test data collapsing the rich fallback structure).
      if (v && v.about) setCms(v.about);
    });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);

  const sec = makeSectionCopyPicker(sc, isAr);
  const title = sec('about_title', isAr ? aboutContent.title : aboutContent.titleEn);
  const cmsBody = pickCmsText(cms, isAr, 60);
  const body    = cmsBody || (isAr ? aboutContent.content : aboutContent.contentEn);

  return (
    <section id="about" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-cyan-50/30 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900" />
      
      <div className="absolute top-0 left-0 right-0 h-24 overflow-hidden">
        <svg className="absolute w-full h-full" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path 
            d="M0,50 Q300,0 600,50 T1200,50 L1200,0 L0,0 Z" 
            className="fill-white dark:fill-slate-950"
            opacity="0.3"
          />
          <path 
            d="M0,70 Q300,20 600,70 T1200,70 L1200,0 L0,0 Z" 
            className="fill-white dark:fill-slate-950"
            opacity="0.2"
          />
        </svg>
      </div>

      <div className="relative container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <div className="inline-flex items-center gap-3 mb-4">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">
                {title}
              </h2>
            </div>
          </div>

          <div className="relative group">
            <div className="relative bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl 
                          rounded-3xl p-8 md:p-12 shadow-2xl 
                          border border-cyan-100 dark:border-slate-700
                          transition-all duration-500 hover:shadow-cyan-500/20">
              
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-400/20 to-transparent rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-400/20 to-transparent rounded-full blur-2xl" />

              <div className={`relative z-10 ${isAr ? 'text-right' : 'text-left'} space-y-6`}>
                {body.split('\n\n').map((paragraph, index) => (
                  <p 
                    key={index}
                    className="text-lg md:text-xl leading-relaxed text-slate-700 dark:text-slate-300
                             animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {paragraph.trim()}
                  </p>
                ))}
              </div>

              <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-cyan-400/40 rounded-full animate-float-slow" />
                <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-blue-400/30 rounded-full animate-float-slow animation-delay-1000" />
                <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-cyan-300/40 rounded-full animate-float-slow animation-delay-2000" />
              </div>
            </div>

            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-cyan-400/0 via-cyan-400/20 to-cyan-400/0 
                          opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10" />
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 overflow-hidden rotate-180">
        <svg className="absolute w-full h-full" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path 
            d="M0,50 Q300,0 600,50 T1200,50 L1200,0 L0,0 Z" 
            className="fill-white dark:fill-slate-950"
            opacity="0.3"
          />
        </svg>
      </div>
    </section>
  );
};

export default About;
